import React from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import {
  CreateFlatStore,
  MIN_FLAT_ATTRIBUTES,
  MAX_FLAT_ATTRIBUTES,
} from "../../hooks/useCreateFlatStore";
import { FLAT_ATTRIBUTES } from "../../constants/FlatAttributes";

interface Props {
  store: CreateFlatStore;
}

const AttributesStep: React.FC<Props> = ({ store }) => {
  const { t } = useTranslation();
  const {
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
  } = useColors();

  const selectedCount = store.flatAttributes.length;
  const canSelectMore = selectedCount < MAX_FLAT_ATTRIBUTES;

  return (
    <StepWrapper
      title={
        t("flats_tab.add_flat.flat_attributes_title") ||
        "What describes your flat?"
      }
      subtitle={`${t("select") || "Select"} ${MIN_FLAT_ATTRIBUTES}–${MAX_FLAT_ATTRIBUTES} ${t("attributes") || "attributes"}`}
    >
      <div>
        {/* Selection counter */}
        <div className="flex justify-center mb-5">
          <div
            className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
            style={{
              backgroundColor:
                selectedCount >= MIN_FLAT_ATTRIBUTES
                  ? "rgba(91, 61, 246, 0.08)"
                  : "rgba(2, 2, 16, 0.06)",
              color:
                selectedCount >= MIN_FLAT_ATTRIBUTES
                  ? accentColor
                  : textNeutralMuted,
            }}
          >
            {selectedCount} {t("selected") || "selected"}
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 justify-center w-0 min-w-full">
          {FLAT_ATTRIBUTES.map((attribute) => {
            const isSelected = store.flatAttributes.includes(
              attribute.id.toString(),
            );
            const isDisabled = !isSelected && !canSelectMore;

            return (
              <button
                key={attribute.id}
                onClick={() => {
                  if (!isDisabled || isSelected) {
                    store.toggleAttribute(attribute.id.toString());
                  }
                }}
                disabled={isDisabled && !isSelected}
                className="cf-pill text-sm disabled:opacity-35 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: isSelected
                    ? accentColor
                    : "rgba(2, 2, 16, 0.05)",
                  color: isSelected ? "white" : textNeutralDefault,
                  borderColor: isSelected
                    ? accentColor
                    : "rgba(2, 2, 16, 0.08)",
                }}
              >
                {attribute.emoji}{" "}
                {t(`flat_attributes.${attribute.key}`) || attribute.label}
              </button>
            );
          })}
        </div>

        {selectedCount > 0 && selectedCount < MIN_FLAT_ATTRIBUTES && (
          <p
            className="mt-5 text-sm text-center"
            style={{ color: textNeutralMuted }}
          >
            {t("select_at_least") || "Please select at least"}{" "}
            {MIN_FLAT_ATTRIBUTES} {t("attributes") || "attributes"}
          </p>
        )}
      </div>
    </StepWrapper>
  );
};

export default AttributesStep;
